import {
  SESSION_EXPIRED_KEY,
  resolveSessionPolicy,
} from '@nxsys/constants/session';
import { appConfigManager } from '@nxsys/utils/appConfigs';

/**
 * Bridge between the axios interceptor and React (NX-5238).
 *
 * ApiConfig has to react to a 401 by ending the session, but it is a plain
 * module: it has no dispatch and no router. Importing the store here instead
 * would close a cycle — store → slices → actions → ApiConfig — so the app
 * registers a handler on mount and the interceptor calls whatever is registered.
 *
 * Until something registers (or if the tree has already unmounted) the fallback
 * still gets the user to the login screen, just without the Redux teardown.
 */

type ExpiryHandler = () => void;

let handler: ExpiryHandler | null = null;

/** Guards against a burst of concurrent 401s each starting their own sign-out. */
let expiring = false;

export function registerSessionExpiryHandler(fn: ExpiryHandler): () => void {
  handler = fn;
  return () => {
    if (handler === fn) handler = null;
  };
}

/** Lets the app clear the latch once it has finished signing the user out. */
export function resetSessionExpiryLatch(): void {
  expiring = false;
}

const SIGN_IN_PATH = '/signin';

/**
 * Names the screen to come back to on the login URL. Exported because every
 * redirect away from the login page has to drop it through the router, the way
 * the handoff parameters are dropped.
 */
export const RETURN_URL_PARAM = 'returnUrl';

/**
 * Screens that must not be resumed after signing back in: landing on one of
 * them would either bounce straight back to the login form or replay a
 * single-use link.
 */
const NON_RESUMABLE_PREFIXES = [SIGN_IN_PATH, '/forgot'];

function isResumable(pathname: string): boolean {
  return !NON_RESUMABLE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * The login URL to send an expired session to, naming the screen to come back to.
 *
 * Built here rather than at each sign-out because both routes into a sign-out —
 * the idle timer's and the interceptor's — still have the original page in the
 * address bar when they reach this point, so neither has to pass it along.
 */
export function signInUrlWithReturn(): string {
  const { pathname, search } = window.location;
  if (!isResumable(pathname)) return SIGN_IN_PATH;
  const params = new URLSearchParams({ [RETURN_URL_PARAM]: pathname + search });
  return `${SIGN_IN_PATH}?${params}`;
}

/**
 * Records why the user is about to land on the login screen, and how long the
 * window that closed was, so the notice there names the period the server is
 * actually applying. The number cannot be written into the message: the window
 * is served through /app-configs and can be changed without rebuilding.
 *
 * Callers that know the policy pass it; the interceptor does not, so it falls
 * back to the last configuration the app was given.
 */
export function markSessionExpired(idleMs?: number): void {
  const ms =
    idleMs ??
    resolveSessionPolicy(appConfigManager.getConfig('session')).idleMs;
  try {
    sessionStorage.setItem(
      SESSION_EXPIRED_KEY,
      String(Math.max(1, Math.round(ms / 60000))),
    );
  } catch {
    // Private-mode storage failures must not stop the sign-out itself.
  }
}

/**
 * The screen to come back to, as named on the login URL, or null if there is
 * nothing that may safely be returned to.
 *
 * The value is treated as hostile rather than as something this app wrote,
 * because the login URL is a link anyone can send. Only an unambiguously
 * relative path is accepted, which is what stops
 * `?returnUrl=https://elsewhere.example` from redirecting a freshly signed-in
 * session off the site. The character after the leading slash is checked too:
 * `//elsewhere.example` is protocol-relative, and a backslash gets folded into a
 * slash by the browser, so `/\elsewhere.example` arrives as the same thing.
 */
export function readReturnPath(): string | null {
  const raw = new URLSearchParams(window.location.search).get(RETURN_URL_PARAM);
  if (!raw || !/^\/[^/\\]/.test(raw)) return null;
  return isResumable(raw.split(/[?#]/)[0]) ? raw : null;
}

let capturedReturnPath: string | null = null;

/**
 * Takes the screen to come back to off the login URL and holds it until the app
 * is ready to act on it.
 *
 * The login URL cannot simply be read later. `/signin` is a public route, so the
 * moment the sign-in succeeds RouteGenerator re-renders it as a plain `Navigate`
 * to the default page, which drops the query string — and that happens while
 * AppLayout is still fetching the profile, the configs and the permissions it
 * needs before it can decide whether this user may open the screen at all.
 */
export function captureReturnPath(): void {
  capturedReturnPath = readReturnPath();
}

/** The captured screen, surrendered to the first caller and not kept after. */
export function takeReturnPath(): string | null {
  const path = capturedReturnPath;
  capturedReturnPath = null;
  return path;
}

/**
 * Ends the session because the server no longer accepts the token. Safe to call
 * from several failing requests at once — only the first does anything.
 */
export function handleSessionExpired(): void {
  if (expiring) return;
  expiring = true;

  // The handler records the reason itself, after its own teardown. Recording it
  // here too would be writing the reason before the sign-out that clears it.
  if (handler) {
    handler();
    return;
  }

  const signInUrl = signInUrlWithReturn();
  markSessionExpired();
  try {
    localStorage.clear();
  } catch {
    // As above — proceed to the redirect regardless.
  }
  window.location.replace(signInUrl);
}
